<template>
  <!-- Floating Game Button - positioned ABOVE the chatbot FAB -->
  <button
    v-if="!isOpen && hasAnyGame"
    @click="isOpen = true"
    class="game-fab"
  >
    <span class="game-fab-icon">🎮</span>
    <span class="game-fab-pulse"></span>
  </button>

  <!-- Game Hub Overlay -->
  <Teleport to="body">
    <Transition name="slide-up">
      <div v-if="isOpen" class="game-overlay" @click.self="closeHub">
        <div class="game-panel">
          <!-- Header -->
          <div class="panel-header">
            <h2 class="text-xl font-bold text-white flex items-center gap-2">
              <span>🎮</span> {{ t('gamification.gameHub.title') }}
            </h2>
            <button @click="closeHub" class="close-btn">✕</button>
          </div>

          <!-- Game Selection -->
          <div v-if="!activeGame" class="game-grid">
            <!-- Active Table Game Banner -->
            <button
              v-if="activeTableGame"
              @click="joinActiveGame"
              class="join-banner"
            >
              <div class="join-pulse"></div>
              <div class="relative z-10 flex items-center gap-3 w-full">
                <div class="text-2xl">🔴</div>
                <div class="flex-1 text-left">
                  <p class="text-white font-bold text-sm">{{ t('gamification.gameHub.activeGame') }}</p>
                  <p class="text-white/60 text-xs">{{ activeTableGame.gameType || 'Gruppenspiel' }} – {{ t('gamification.gameHub.joinNow') }}</p>
                </div>
                <span class="join-arrow">→</span>
              </div>
            </button>

            <!-- Promo Banner -->
            <div class="promo-banner">
              <div class="promo-glow"></div>
              <div class="relative z-10 flex items-center gap-3">
                <div class="text-3xl">🎁</div>
                <div class="flex-1">
                  <p class="text-white font-bold text-sm leading-tight">{{ t('gamification.gameHub.subtitle') }}</p>
                  <p class="text-white/50 text-xs mt-0.5">{{ t('gamification.gameHub.subtitle') }}</p>
                </div>
              </div>
            </div>

            <!-- Premium Mini-Games Section -->
            <div class="section-label">{{ t('gamification.gameHub.premiumGames') }}</div>

            <button @click="activeGame = 'rush-hour'" class="game-card premium">
              <div class="game-card-icon premium-icon">⚡</div>
              <div class="game-card-content">
                <h3 class="font-bold text-white">{{ t('gamification.games.rushHour') }}</h3>
                <p class="text-xs text-white/60">{{ t('gamification.games.rushHourDesc') }}</p>
              </div>
              <span class="game-badge both">{{ t('gamification.gameHub.solo') }}/{{ t('gamification.gameHub.table') }}</span>
            </button>

            <button @click="activeGame = 'golden-pour'" class="game-card premium">
              <div class="game-card-icon premium-icon">🥂</div>
              <div class="game-card-content">
                <h3 class="font-bold text-white">{{ t('gamification.games.goldenPour') }}</h3>
                <p class="text-xs text-white/60">{{ t('gamification.games.goldenPourDesc') }}</p>
              </div>
              <span class="game-badge both">{{ t('gamification.gameHub.solo') }}/{{ t('gamification.gameHub.table') }}</span>
            </button>

            <button @click="activeGame = 'order-blitz'" class="game-card premium">
              <div class="game-card-icon premium-icon">⚡</div>
              <div class="game-card-content">
                <h3 class="font-bold text-white">{{ t('gamification.games.orderBlitz') }}</h3>
                <p class="text-xs text-white/60">{{ t('gamification.games.orderBlitzDesc') }}</p>
              </div>
              <span class="game-badge both">{{ t('gamification.gameHub.solo') }}/{{ t('gamification.gameHub.table') }}</span>
            </button>

            <button @click="activeGame = 'shell-game'" class="game-card premium">
              <div class="game-card-icon premium-icon">🏆</div>
              <div class="game-card-content">
                <h3 class="font-bold text-white">{{ t('gamification.games.shellGame') }}</h3>
                <p class="text-xs text-white/60">{{ t('gamification.games.shellGameDesc') }}</p>
              </div>
              <span class="game-badge solo">Solo</span>
            </button>

            <!-- Classic Games Section -->
            <div class="section-label mt-2">{{ t('gamification.gameHub.miniGames') }}</div>

            <button
              v-if="features.lucky_wheel"
              @click="activeGame = 'lucky-wheel'"
              class="game-card"
            >
              <div class="game-card-icon">🎡</div>
              <div class="game-card-content">
                <h3 class="font-bold text-white">{{ t('gamification.games.luckyWheel') }}</h3>
                <p class="text-xs text-white/60">{{ t('gamification.games.luckyWheelDesc') }}</p>
              </div>
              <span class="game-badge solo">Solo</span>
            </button>

            <button
              v-if="features.quiz_battle"
              @click="activeGame = 'quiz'"
              class="game-card"
            >
              <div class="game-card-icon">🧠</div>
              <div class="game-card-content">
                <h3 class="font-bold text-white">{{ t('gamification.games.quizBattle') }}</h3>
                <p class="text-xs text-white/60">{{ t('gamification.games.quizBattleDesc') }}</p>
              </div>
              <span class="game-badge both">{{ t('gamification.gameHub.solo') }}/{{ t('gamification.gameHub.table') }}</span>
            </button>

            <button
              v-if="features.food_tinder"
              @click="activeGame = 'food-tinder'"
              class="game-card"
            >
              <div class="game-card-icon">😋</div>
              <div class="game-card-content">
                <h3 class="font-bold text-white">{{ t('gamification.games.foodTinder') }}</h3>
                <p class="text-xs text-white/60">{{ t('gamification.games.foodTinderDesc') }}</p>
              </div>
              <span class="game-badge solo">Solo</span>
            </button>

            <button
              v-if="features.roulette"
              @click="activeGame = 'roulette'"
              class="game-card"
            >
              <div class="game-card-icon">🎰</div>
              <div class="game-card-content">
                <h3 class="font-bold text-white">{{ t('gamification.games.billRoulette') }}</h3>
                <p class="text-xs text-white/60">{{ t('gamification.games.billRouletteDesc') }}</p>
              </div>
              <span class="game-badge group">Gruppe</span>
            </button>
          </div>

          <!-- Active Game -->
          <div v-else class="active-game">
            <!-- Game header with back + close -->
            <div class="active-game-header">
              <button @click="leaveGame" class="back-btn">{{ t('gamification.gameHub.back') }}</button>
              <button @click="closeHub" class="exit-btn">{{ t('gamification.gameHub.close') }}</button>
            </div>

            <component
              :is="activeGameComponent"
              :key="activeGame"
              v-bind="activeGameProps"
              @close="closeHub"
              @apply-discount="handleApplyDiscount"
              @add-to-cart="handleDirectAddToCart"
              @go-to-checkout="goToCheckout"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <GameReward
    :visible="showReward"
    :reward="pendingReward"
    :game-name="rewardGameName"
    @redeem="redeemReward"
    @dismiss="dismissReward"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGamificationStore } from '@/stores/gamification'
import { useCartStore } from '@/stores/cart'
import { gamificationService } from '@/services/api/gamificationService'
import LuckyWheel from './LuckyWheel.vue'
import QuizBattle from './QuizBattle.vue'
import FoodTinder from './FoodTinder.vue'
import BillRoulette from './BillRoulette.vue'
import RushHourGame from './RushHourGame.vue'
import GoldenPourGame from './GoldenPourGame.vue'
import OrderBlitzGame from './OrderBlitzGame.vue'
import ShellGame from './ShellGame.vue'
import GameReward from './GameReward.vue'
import { areCustomerGamesEnabled } from '@/utils/customerGames'

const emit = defineEmits(['add-to-cart', 'apply-discount'])
const { t } = useI18n()
const gamificationStore = useGamificationStore()
const cartStore = useCartStore()
const route = useRoute()
const router = useRouter()

const sessionNumericId = computed(() => {
  return cartStore.sessionNumericId || parseInt(localStorage.getItem('sessionNumericId')) || null
})
const sessionUniqueId = computed(() => {
  return cartStore.sessionUniqueId || localStorage.getItem('sessionUniqueId') || localStorage.getItem('sessionId') || ''
})
const tableId = computed(() => {
  const storeVal = cartStore.tableId
  if (storeVal != null) return storeVal
  return parseInt(localStorage.getItem('tableId')) || null
})

const isOpen = ref(false)
const activeGame = ref(null)
const showReward = ref(false)
const pendingReward = ref(null)
const rewardGameName = ref('')
const activeTableGame = ref(null)
const joiningExisting = ref(false)
let pollInterval = null
let lastKnownSession = null

function checkOpenFlag() {
  if (!areCustomerGamesEnabled()) {
    localStorage.removeItem('openGameHub')
    isOpen.value = false
    return
  }
  if (localStorage.getItem('openGameHub') === 'true') {
    localStorage.removeItem('openGameHub')
    isOpen.value = true
  }
}

function handleOpenEvent() {
  if (!areCustomerGamesEnabled()) return
  isOpen.value = true
}

async function checkActiveTableGames() {
  const tid = tableId.value
  if (!tid || tid === 0) return

  try {
    const { data } = await gamificationService.getActiveMiniGameForTable(tid)
    if (data && data.id && ['waiting', 'playing'].includes(data.status)) {
      activeTableGame.value = data
    } else {
      activeTableGame.value = null
    }
  } catch (e) {
    activeTableGame.value = null
  }

  if (!activeTableGame.value && tid) {
    try {
      const { data } = await gamificationService.getActiveRouletteForTable(tid)
      if (data && data.id && ['waiting', 'spinning'].includes(data.status)) {
        activeTableGame.value = {
          gameType: 'Rechnungs-Roulette',
          type: 'roulette',
          id: data.id,
        }
      }
    } catch (e) { /* no active roulette */ }
  }
}

function joinActiveGame() {
  if (!activeTableGame.value) return
  joiningExisting.value = true

  if (activeTableGame.value.type === 'roulette') {
    activeGame.value = 'roulette'
  } else {
    const gameType = activeTableGame.value.gameType
    if (gameType === 'rush_hour') activeGame.value = 'rush-hour'
    else if (gameType === 'golden_pour') activeGame.value = 'golden-pour'
    else if (gameType === 'order_blitz') activeGame.value = 'order-blitz'
    else activeGame.value = 'rush-hour'
  }
}

function resetGameState() {
  activeGame.value = null
  activeTableGame.value = null
  joiningExisting.value = false
  showReward.value = false
  pendingReward.value = null
  isOpen.value = false
}

function detectSessionChange() {
  const currentSession = sessionUniqueId.value
  if (lastKnownSession && lastKnownSession !== currentSession) {
    resetGameState()
  }
  lastKnownSession = currentSession
}

onMounted(() => {
  if (!areCustomerGamesEnabled()) {
    localStorage.removeItem('openGameHub')
    return
  }
  lastKnownSession = sessionUniqueId.value
  checkOpenFlag()
  window.addEventListener('open-game-hub', handleOpenEvent)
  checkActiveTableGames()
  pollInterval = setInterval(() => {
    detectSessionChange()
    checkActiveTableGames()
  }, 8000)
})

onUnmounted(() => {
  window.removeEventListener('open-game-hub', handleOpenEvent)
  if (pollInterval) clearInterval(pollInterval)
})

watch(() => route.path, () => {
  setTimeout(checkOpenFlag, 150)
  detectSessionChange()
})

watch(sessionUniqueId, (newVal, oldVal) => {
  if (oldVal && newVal && oldVal !== newVal) {
    resetGameState()
    checkActiveTableGames()
  }
})

watch(isOpen, (val) => {
  if (val) {
    checkActiveTableGames()
    joiningExisting.value = false
  }
})

const features = computed(() => gamificationStore.features)
const hasAnyGame = computed(() => areCustomerGamesEnabled())

const gameComponentMap = {
  'rush-hour': RushHourGame,
  'golden-pour': GoldenPourGame,
  'order-blitz': OrderBlitzGame,
  'shell-game': ShellGame,
  'lucky-wheel': LuckyWheel,
  'quiz': QuizBattle,
  'food-tinder': FoodTinder,
  'roulette': BillRoulette,
}

const activeGameComponent = computed(() => {
  if (!activeGame.value) return null
  return gameComponentMap[activeGame.value] || null
})

const activeGameProps = computed(() => {
  const game = activeGame.value
  if (!game) return {}

  const existingId = joiningExisting.value ? (activeTableGame.value?.id || null) : null

  if (['rush-hour', 'golden-pour', 'order-blitz'].includes(game)) {
    return { existingGameId: existingId }
  }
  if (game === 'roulette') {
    return {
      visible: true,
      orderSessionId: sessionNumericId.value,
      sessionIdentifier: sessionUniqueId.value,
      tableId: tableId.value,
      isHost: !joiningExisting.value,
      gameId: joiningExisting.value && activeTableGame.value ? activeTableGame.value.id : null,
    }
  }
  return {}
})

function leaveGame() {
  activeGame.value = null
  joiningExisting.value = false
  activeTableGame.value = null
  checkActiveTableGames()
}

function closeHub() {
  activeGame.value = null
  isOpen.value = false
  joiningExisting.value = false
  activeTableGame.value = null
}

function goToCheckout() {
  closeHub()
  const sid = sessionUniqueId.value
  const tid = tableId.value
  if (tid) {
    router.push(`/cart/${tid}/${sid}`)
  } else {
    router.push(`/cart/take-out/${sid}`)
  }
}

function handleDirectAddToCart(product) {
  if (!product || !product.id) return

  const itemToAdd = {
    ...product,
    price: product.isFreeReward ? 0 : product.price,
    name: product.isFreeReward
      ? `${product.name} (GRATIS - ${product.rewardSource || 'Spiel'})`
      : product.name,
    description: product.isFreeReward
      ? `Gratis gewonnen bei: ${product.rewardSource || 'Spiel'}`
      : (product.description || ''),
  }

  cartStore.addItem(itemToAdd, 1)
  console.log('[GameHub] Item added to cart:', itemToAdd.name, 'price:', itemToAdd.price)
  emit('add-to-cart', itemToAdd)
}

function handleApplyDiscount(value) {
  pendingReward.value = typeof value === 'object'
    ? value
    : { type: 'discount', value, message: `${value}% Rabatt auf deine nächste Bestellung!` }
  rewardGameName.value = (pendingReward.value && pendingReward.value.gameName) || 'Mini-Spiel'
  showReward.value = true
}

function redeemReward() {
  const reward = pendingReward.value
  if (!reward) {
    showReward.value = false
    return
  }

  try {
    if (reward.type === 'freeItem' && reward.product) {
      const productName = reward.product.name || reward.productName || 'Gratis-Artikel'
      cartStore.addItem({
        id: reward.product.id || reward.product.productId || `free-${Date.now()}`,
        name: `${productName} (GRATIS - ${reward.gameName || 'Spiel'})`,
        price: 0,
        description: `Gratis gewonnen bei: ${reward.gameName || 'Mini-Spiel'}`,
        imageUrl: reward.product.imageUrl || reward.product.image_url || '',
      }, 1, [], '')
    } else if (reward.type === 'discount' && reward.value && reward.product) {
      const originalPrice = parseFloat(reward.product.price || 0)
      const discountedPrice = Math.max(0, originalPrice * (1 - reward.value / 100))
      cartStore.addItem({
        id: reward.product.id || `disc-${Date.now()}`,
        name: `${reward.product.name} (${reward.value}% Rabatt)`,
        price: discountedPrice,
        description: `Originalpreis: €${originalPrice.toFixed(2)} – ${reward.value}% Rabatt (${reward.gameName || 'Spiel'})`,
        imageUrl: reward.product.imageUrl || reward.product.image_url || '',
      }, 1, [], '')
    } else if (reward.type === 'discount' && reward.value) {
      cartStore.addItem({
        id: `voucher-${Date.now()}`,
        name: `${reward.value}% Rabatt-Gutschein`,
        price: 0,
        description: `${reward.gameName || 'Spiel'}-Belohnung – wird beim Checkout angewendet`,
        isDiscount: true,
        discountPercent: reward.value,
      }, 1, [], '')
    }
  } catch (e) {
    console.error('Reward einlösen fehlgeschlagen:', e)
  }

  emit('apply-discount', reward)
  showReward.value = false
  pendingReward.value = null
  closeHub()
}

function dismissReward() {
  showReward.value = false
  pendingReward.value = null
}
</script>

<style scoped>
.game-fab {
  position: fixed;
  bottom: 10.5rem;
  right: 1rem;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 9999px;
  z-index: 45;
  background: linear-gradient(135deg, #7c3aed, #4338ca);
  box-shadow: 0 4px 20px rgba(124, 58, 237, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}
.game-fab:hover { transform: scale(1.1); }
.game-fab:active { transform: scale(0.95); }
.game-fab-icon {
  font-size: 1.5rem;
  position: relative;
  z-index: 10;
}
.game-fab-pulse {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  background: rgba(139, 92, 246, 0.5);
  animation: fabPulse 2s infinite;
}

.game-overlay {
  position: fixed;
  inset: 0;
  z-index: 9990;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.game-panel {
  width: 100%;
  max-width: 32rem;
  max-height: 92vh;
  overflow-y: auto;
  background: linear-gradient(180deg, #0a0a14, #12122a);
  border-radius: 1.5rem 1.5rem 0 0;
  padding: 1.25rem;
  padding-bottom: 2rem;
  -webkit-overflow-scrolling: touch;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.close-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  font-size: 0.875rem;
}
.close-btn:hover { background: rgba(255,255,255,0.2); }

/* Active table game join banner */
.join-banner {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 1rem;
  overflow: hidden;
  margin-bottom: 0.25rem;
  background: linear-gradient(135deg, rgba(239,68,68,0.15), rgba(220,38,38,0.1));
  border: 1px solid rgba(239,68,68,0.3);
  transition: all 0.2s ease;
}
.join-banner:active { transform: scale(0.98); }
.join-pulse {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: #ef4444;
  animation: livePulse 1.5s infinite;
}
.join-arrow {
  color: rgba(239,68,68,0.8);
  font-size: 1.25rem;
  font-weight: 700;
}

.promo-banner {
  position: relative;
  padding: 1rem;
  border-radius: 1rem;
  overflow: hidden;
  margin-bottom: 0.25rem;
  background: linear-gradient(135deg, rgba(124,58,237,0.15), rgba(79,70,229,0.1));
  border: 1px solid rgba(124,58,237,0.2);
}
.promo-glow {
  position: absolute;
  top: -1rem;
  right: -1rem;
  width: 6rem;
  height: 6rem;
  border-radius: 9999px;
  background: radial-gradient(circle, rgba(250,204,21,0.15), transparent 70%);
}

.section-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.25);
  font-weight: 700;
  padding: 0 0.25rem;
  margin-top: 0.25rem;
  margin-bottom: 0.125rem;
}

.game-grid {
  display: grid;
  gap: 0.625rem;
}

.game-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem;
  border-radius: 1rem;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  transition: all 0.3s ease;
}
.game-card:hover {
  background: rgba(255,255,255,0.07);
  border-color: rgba(255,255,255,0.12);
}
.game-card:active { transform: scale(0.98); }
.game-card.premium {
  border-color: rgba(139,92,246,0.2);
  background: rgba(139,92,246,0.04);
}
.game-card.premium:hover {
  background: rgba(139,92,246,0.08);
  border-color: rgba(139,92,246,0.3);
}
.game-card-icon {
  font-size: 1.5rem;
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.06);
  border-radius: 0.75rem;
  flex-shrink: 0;
}
.game-card-icon.premium-icon {
  background: linear-gradient(135deg, rgba(139,92,246,0.2), rgba(99,102,241,0.2));
  box-shadow: 0 0 12px rgba(124,58,237,0.15);
}
.game-card-content {
  flex: 1;
  text-align: left;
}
.game-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  flex-shrink: 0;
}
.game-badge.solo {
  background: rgba(139,92,246,0.15);
  color: rgba(196,167,231,0.8);
}
.game-badge.group {
  background: rgba(6,182,212,0.15);
  color: rgba(103,232,249,0.8);
}
.game-badge.both {
  background: rgba(234,179,8,0.15);
  color: rgba(253,224,71,0.8);
}

/* Active game header with back + close */
.active-game-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.back-btn {
  color: rgba(255,255,255,0.6);
  font-size: 0.875rem;
  transition: color 0.2s;
}
.back-btn:hover { color: rgba(255,255,255,0.9); }
.exit-btn {
  color: rgba(239,68,68,0.7);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(239,68,68,0.1);
  border: 1px solid rgba(239,68,68,0.2);
  transition: all 0.2s;
}
.exit-btn:hover {
  background: rgba(239,68,68,0.2);
  color: rgba(239,68,68,0.9);
}

.active-game {
  margin-top: 0.5rem;
}

.slide-up-enter-active { transition: all 0.3s ease-out; }
.slide-up-leave-active { transition: all 0.2s ease-in; }
.slide-up-enter-from { opacity: 0; }
.slide-up-leave-to { opacity: 0; }
.slide-up-enter-from .game-panel { transform: translateY(100%); }
.slide-up-leave-to .game-panel { transform: translateY(100%); }

@keyframes fabPulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.4); opacity: 0; }
}

@keyframes livePulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.5); }
}
</style>
